import { SimpleMarkdown } from '../../../components/ui/SimpleMarkdown';
import { formatDayMonthYear } from '../../../utils/dateFormat';
import { companyDisplay } from './ResearchRunList';
import type { ResearchClaim, SalesResearchReport, SalesResearchRun } from '../../../api/salesResearch';
import { ClaimConfidencePanel } from './ClaimConfidencePanel';
import { OutreachAngleCard, PersonaMessagingCards } from './OutreachAngleCard';
import { SourceStatusList } from './SourceStatusList';

/** Sections rendered from claim rows, shown after the prose rather than inline. */
const CAVEAT_SECTIONS = new Set(['confirm', 'conflicts', 'sources']);
/** The plan executes the outreach angle, so it reads after it rather than before. */
const CLOSING_SECTION = 'attack_plan';

/** Run-scoped so two briefs on one page cannot collide on a section id. */
function anchor(runId: string, sectionId: string): string {
  return `brief-${runId}-${sectionId}`;
}

interface Props {
  run: SalesResearchRun;
  report: SalesResearchReport;
  claims: ResearchClaim[];
}

export function ResearchReportView({ run, report, claims }: Props) {
  const prose = report.sections.filter(
    (s) => !CAVEAT_SECTIONS.has(s.id) && s.id !== CLOSING_SECTION,
  );
  const caveats = report.sections.filter((s) => CAVEAT_SECTIONS.has(s.id));
  const attackPlan = report.sections.find((s) => s.id === CLOSING_SECTION);

  return (
    <article className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-heading-sm font-semibold">{companyDisplay(run).name}</h2>
        <p className="text-body-sm text-text-tertiary">
          Researched {formatDayMonthYear(run.createdAt)}
          {companyDisplay(run).domain ? ` · ${companyDisplay(run).domain}` : ''}
          {run.leads.length > 0 ? ` · ${run.leads.map((l) => l.name).join(', ')}` : ''}
        </p>
      </header>

      <SourceStatusList statuses={run.sourceStatuses} />

      {/* A brief now runs to a dozen sections. Without a way in, the contact
          tables and the country counts (the parts a rep opens the brief for)
          are several screens below the fold. */}
      {prose.length > 3 && (
        <nav className="flex flex-wrap gap-x-3 gap-y-1 rounded-md border border-border bg-surface-secondary px-3 py-2">
          {[...prose, ...(attackPlan ? [attackPlan] : [])].map((section) => (
            <a
              key={section.id}
              href={`#${anchor(run.id, section.id)}`}
              className="text-body-xs text-primary hover:underline"
            >
              {section.heading}
            </a>
          ))}
        </nav>
      )}

      {prose.map((section) => (
        <section key={section.id} id={anchor(run.id, section.id)} className="scroll-mt-4 space-y-1">
          <h3 className="text-body-md font-semibold">{section.heading}</h3>
          <div className="text-body-sm leading-relaxed">
            <SimpleMarkdown content={section.body} />
          </div>
        </section>
      ))}

      {report.leadBriefs.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-body-md font-semibold">Lead context</h3>
          {report.leadBriefs.map((brief) => (
            <div key={brief.name} className="rounded-md border border-border p-3">
              <h4 className="text-body-sm font-semibold">{brief.name}</h4>
              <div className="text-body-sm leading-relaxed">
                <SimpleMarkdown content={brief.body} />
              </div>
            </div>
          ))}
        </section>
      )}

      {report.outreach ? (
        <OutreachAngleCard outreach={report.outreach} />
      ) : (
        // Rendering nothing here hid the absence of the single section reps act
        // on — the brief looked complete while its conclusion was missing.
        <section className="rounded-md border border-warning/20 bg-warning-light p-3">
          <h3 className="text-body-md font-semibold">Outreach angle unavailable</h3>
          <p className="text-body-sm text-muted-foreground">
            The research completed but the outreach angle could not be generated. Everything above
            still stands: the trigger, pain hypothesis and messaging need to be written by hand for
            this account, or the run repeated.
          </p>
        </section>
      )}

      {attackPlan && (
        <section id={anchor(run.id, attackPlan.id)} className="scroll-mt-4 space-y-1">
          <h3 className="text-body-md font-semibold">{attackPlan.heading}</h3>
          <div className="text-body-sm leading-relaxed">
            <SimpleMarkdown content={attackPlan.body} />
          </div>
        </section>
      )}

      {report.outreach && <PersonaMessagingCards outreach={report.outreach} />}

      {caveats.map((section) => (
        <section key={section.id} className="space-y-1">
          <h3 className="text-body-md font-semibold">{section.heading}</h3>
          <div className="text-body-sm leading-relaxed">
            <SimpleMarkdown content={section.body} />
          </div>
        </section>
      ))}

      <ClaimConfidencePanel claims={claims} />

      {report.citations.length > 0 && (
        <details className="rounded-md border border-border px-3 py-2">
          <summary className="cursor-pointer text-body-sm font-medium">
            All sources ({report.citations.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {report.citations.map((c) => (
              <li key={c.url}>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-body-xs text-primary underline break-all"
                >
                  {c.title ?? c.url}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </article>
  );
}
